import { useEffect } from 'react';

import { refreshSessionSingleFlight } from '../../api/tokenRefresh';
import { getTokenExpiryMs } from './jwt';

/** Renew this long before the access token expires so no request races the expiry. */
const RENEW_MARGIN_MS = 2 * 60 * 1000;
/** Cadence used when the access token carries no readable `exp` claim. */
const FALLBACK_INTERVAL_MS = 5 * 60 * 1000;
/** Backend unreachable: keep retrying so the refresh token never quietly ages out. */
const RETRY_INTERVAL_MS = 30 * 1000;
const MIN_DELAY_MS = 1000;

function delayUntilRenewal(accessToken: string): number {
  const expiry = getTokenExpiryMs(accessToken);
  if (expiry === null) {
    return FALLBACK_INTERVAL_MS;
  }
  return Math.max(expiry - RENEW_MARGIN_MS - Date.now(), MIN_DELAY_MS);
}

function isRenewalDue(accessToken: string): boolean {
  const expiry = getTokenExpiryMs(accessToken);
  return expiry !== null && expiry - RENEW_MARGIN_MS <= Date.now();
}

/**
 * Keeps the admin session alive for as long as the browser is open: renews the
 * access token shortly before it expires (which also rotates the refresh token,
 * sliding its expiry forward) and catches up after sleep, tab wake-ups, and
 * connectivity loss.
 */
export function useSessionKeepAlive(accessToken: string | null): void {
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    let cancelled = false;
    let timer = 0;

    const schedule = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void renew(), delay);
    };

    async function renew(): Promise<void> {
      const outcome = await refreshSessionSingleFlight();
      if (cancelled) {
        return;
      }
      if (outcome.status === 'unavailable') {
        schedule(RETRY_INTERVAL_MS);
      }
      // 'refreshed': the new token re-runs this effect and reschedules.
      // 'expired': AuthProvider reacts to AUTH_SESSION_EXPIRED_EVENT.
    }

    const renewIfDue = () => {
      if (!cancelled && isRenewalDue(accessToken)) {
        void renew();
      }
    };

    const renewOnWake = () => {
      if (document.visibilityState === 'visible') {
        renewIfDue();
      }
    };

    schedule(delayUntilRenewal(accessToken));
    window.addEventListener('focus', renewIfDue);
    window.addEventListener('online', renewIfDue);
    document.addEventListener('visibilitychange', renewOnWake);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener('focus', renewIfDue);
      window.removeEventListener('online', renewIfDue);
      document.removeEventListener('visibilitychange', renewOnWake);
    };
  }, [accessToken]);
}
